import { useState, useEffect } from 'react';
import type { ArticleData } from '@/mocks/articles';

interface ArticleContentProps {
  article: ArticleData;
}

export default function ArticleContent({ article }: ArticleContentProps) {
  const [activeSection, setActiveSection] = useState(article.sections[0]?.id || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [article.slug]);

  const tocItems = article.sections.map((s) => ({
    id: s.id,
    label: s.title,
  }));

  return (
    <article className="w-full py-10 md:py-14 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 max-w-6xl mx-auto">

          <aside className="lg:w-64 shrink-0">
            <div className="sticky top-24 bg-background-100 rounded-2xl p-5">
              <h3 className="font-heading text-sm font-semibold text-foreground-950 uppercase tracking-wider mb-4">
                Table of Contents
              </h3>
              <nav>
                <ul className="space-y-1">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        onClick={() => setActiveSection(item.id)}
                        className={`block text-sm py-1.5 px-3 rounded-lg cursor-pointer transition-colors duration-150 whitespace-nowrap ${
                          activeSection === item.id
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:text-primary-500 hover:bg-background-200/70'
                        }`}
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="mt-6 pt-4 border-t border-background-200/70">
                <p className="text-xs text-foreground-500 mb-3">Related Resources</p>
                <a href="/" className="block text-sm text-primary-500 hover:text-primary-600 font-medium mb-2 cursor-pointer transition-colors">
                  Back to Homepage
                </a>
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="block text-sm text-foreground-600 hover:text-primary-500 cursor-pointer transition-colors">
                  Visit KasPreadsheet
                </a>
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="text-foreground-800 leading-relaxed">

              {article.sections.map((section) => (
                <section key={section.id} id={section.id} className="mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    {section.title}
                  </h2>
                  {section.content.map((paragraph, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-sm md:text-base text-foreground-700 mb-4"
                    >
                      {paragraph}
                    </p>
                  ))}
                </section>
              ))}

              {article.faqs.length > 0 && (
                <section className="mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {article.faqs.map((item) => (
                      <details key={item.q} className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer">
                        <summary className="p-5 font-heading text-base font-semibold text-foreground-950 flex items-center justify-between">
                          {item.q}
                          <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                        </summary>
                        <div className="px-5 pb-5">
                          <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              <div className="bg-background-100 rounded-2xl p-6 flex flex-col sm:flex-row gap-4 items-start">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-primary-600">LB</span>
                </div>
                <div>
                  <p className="font-heading text-base font-semibold text-foreground-950">LitBuy Spreadsheet Editorial Team</p>
                  <p className="text-xs text-foreground-500 mt-0.5">Updated June 2026</p>
                  <p className="text-sm text-foreground-600 mt-2">
                    Our editorial team combines extensive experience in e-commerce optimization, spreadsheet design, and consumer research. We are passionate about helping shoppers make smarter, more informed purchasing decisions through structured data organization.
                  </p>
                </div>
              </div>

              <div className="mt-10 p-6 bg-secondary-50 rounded-2xl">
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3">
                  Continue Reading
                </h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer transition-colors flex items-center gap-1.5">
                      <i className="ri-home-4-line text-sm"></i>
                      LitBuy Spreadsheet Home &mdash; Browse All Categories and Guides
                    </a>
                  </li>
                  <li>
                    <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-600 hover:text-primary-500 cursor-pointer transition-colors flex items-center gap-1.5">
                      <i className="ri-external-link-line text-sm"></i>
                      Visit KasPreadsheet &mdash; Shop All 14 Categories
                    </a>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </div>
    </article>
  );
}